import { getResultStateByPeriod } from "@/api/financialReport";
import { consoleHandlerError, extractErrorMessage } from "@/utils/message";
import { resolveDateRange, buildHeadersByPeriod, formatPeriodLabel } from "./utils/dateRange";

// Las filas de la tabla las manda el backend en `structure` (armadas desde
// el plan de cuentas de la empresa: use_cases/financial_report/
// income_statement.py). Cada fila trae su clase (`account` / `total` /
// `margin` / `ratio`), su grupo, su etiqueta, su fórmula (keysSum /
// keysSubtract / keyDivide), su padre y su tab destino. Acá solo se
// proyecta al shape que consumen los tabs y se corren las fórmulas.
function rowFromStructure(row) {
    return {
        key: row.key,
        kind: row.kind,
        label: row.label,
        levelOne: row.level_1,
        isMoney: Boolean(row.is_money),
        theme: row.theme || undefined,
        keysSum: row.keys_sum || [],
        keysSubtract: row.keys_subtract || [],
        keyDivide: row.key_divide || "",
        hasChilds: Boolean(row.has_childs),
        parentKey: row.parent_key || null,
        targetTab: row.target_tab || null,
        // `all_keys` del backend = filas visibles; los hijos colapsados
        // (Gastos Totales, No Operacional, Ventas con varias cuentas) llegan
        // ocultos y el tab los despliega con TOGGLE_CHILD_ROWS.
        isShowed: Boolean(row.visible),
        itemsDict: {},
        prevYearDict: {},
        prevPeriodDict: {},
        items: [],
    };
}

// Persistencia de la granularidad (Mensual/Trimestral/Anual/YTD) -----------
// Es el filtro compartido de la barra del reporte (todos los tabs lo leen vía
// rootState.financialReports.filterPeriod). Igual que el rango (reportsFilters),
// lo persistimos en sessionStorage para que sobreviva al reload del cambio de
// empresa y la granularidad elegida sea la misma para cualquier empresa dentro
// de la sesión del tab. Es company-agnostic, así que siempre es válida.
const FILTER_PERIOD_KEY = "reportsFilterPeriod";

function loadFilterPeriod() {
    try {
        return sessionStorage.getItem(FILTER_PERIOD_KEY) || null;
    } catch (e) {
        return null;
    }
}

function persistFilterPeriod(value) {
    try {
        sessionStorage.setItem(FILTER_PERIOD_KEY, value);
    } catch (e) {
        /* noop — sessionStorage no disponible */
    }
}

// El Estado de Resultados siempre pide `detail=cuentas`: cada línea del P&L
// que es una cuenta de nivel 2 viaja además abierta en sus cuentas de nivel 3,
// con los mismos dicts por cabecera que las líneas (`itemsDict` /
// `prevYearDict` / `prevPeriodDict`), así el tab arma las filas hijas con el
// mismo código. (Antes era un modo opcional detrás del botón "Cuentas".)
const DETAIL_CUENTAS = "cuentas";

const state = () => ({
    filterPeriod: loadFilterPeriod() || "month",
    // { <key>: fila } en el orden de `structure`; vacío hasta el primer fetch.
    tableRowHeaders: {},
    netSellsDetail: {},
    maxCostsDetail: {},
    maxExpensesDetail: {},
    // { <key del P&L>: [{ label, itemsDict, prevYearDict, prevPeriodDict }] }
    accountsDetail: {},
    isLoading: false,
    error: null
});

const getters = {
    // Las cabeceras se derivan del MISMO rango con el que `fetchReportData`
    // le pega al backend, vía `resolveDateRange(_, _, filterPeriod)` +
    // `buildHeadersByPeriod`. Antes hacíamos un switch + loop manual con
    // números hardcoded (12 / 24), lo que producía 13 / 25 cabeceras
    // (off-by-one inclusivo) y se desincronizaba cuando el cálculo del
    // rango cambiaba — exactamente el bug que motivó esta refactorización.
    getHeadersTable: (state, _getters, rootState) => () => {
        const { initDate, endDate } = resolveDateRange(
            rootState.reportsFilters.endMonth,
            rootState.reportsFilters.selectedPeriodRange,
            state.filterPeriod,
            rootState.reportsFilters.customRange,
            rootState.reportsFilters.customYearRange,
            rootState.reportsFilters.dataFirstMonth,
        );
        return buildHeadersByPeriod(initDate, endDate, state.filterPeriod);
    }
};

const mutations = {
    SET_FILTER_PERIOD(state, period) {
        state.filterPeriod = period;
        persistFilterPeriod(period);
    },
    SET_REPORT_DATA(state, data) {
        state.tableRowHeaders = data.tableRowHeaders;
        state.netSellsDetail = data.netSellsDetail;
        state.maxCostsDetail = data.maxCostsDetail;
        state.maxExpensesDetail = data.maxExpensesDetail;
        state.accountsDetail = data.accountsDetail || {};
    },
    // Despliega/colapsa las hijas de una fila con `hasChilds`. Acepta el
    // booleano de siempre (hijas de Gastos Totales) o `{ parentKey, isExpanded }`.
    TOGGLE_CHILD_ROWS(state, payload) {
        const { parentKey, isExpanded } = typeof payload === "object" && payload !== null
            ? payload
            : { parentKey: "gastos_totales", isExpanded: Boolean(payload) };
        Object.values(state.tableRowHeaders).forEach(row => {
            if (row.parentKey === parentKey) row.isShowed = isExpanded;
        });
    },
    SET_LOADING(state, isLoading) {
        state.isLoading = isLoading;
    },
    SET_ERROR(state, error) {
        state.error = error;
    }
};

const actions = {
    async fetchReportData({ state, commit, getters, rootState }) {
        commit("SET_LOADING", true);
        // Limpiar el error del fetch anterior: sin esto, tras un fallo el
        // banner de error persistía aunque el refetch siguiente saliera bien.
        commit("SET_ERROR", null);
        try {
            // Mismo cálculo que el getter — pasamos filterPeriod a
            // resolveDateRange para que la regla year/ytd quede aplicada en un
            // solo lugar y nunca se desincronicen cabeceras vs payload.
            const { initDate, endDate } = resolveDateRange(
                rootState.reportsFilters.endMonth,
                rootState.reportsFilters.selectedPeriodRange,
                state.filterPeriod,
                rootState.reportsFilters.customRange,
                rootState.reportsFilters.customYearRange,
                rootState.reportsFilters.dataFirstMonth,
            );

            const response = await getResultStateByPeriod(
                state.filterPeriod, initDate, endDate, DETAIL_CUENTAS,
            );

            const data = response.data.report_data || {};
            const structure = response.data.structure || [];

            const tableRowHeaders = {};
            structure.forEach(row => {
                tableRowHeaders[row.key] = rowFromStructure(row);
            });

            // Cuentas: la data cruda del backend. (Los totales también viajan
            // sumados en report_data, pero se recalculan abajo con la misma
            // fórmula que las demás filas.)
            structure.forEach(row => {
                if (row.kind !== "account") return;
                Object.assign(
                    tableRowHeaders[row.key],
                    buildPeriodDicts(data[row.key], state.filterPeriod),
                );
            });

            // Cuentas L3 por línea (`accounts_detail`). Mismas keys de cabecera
            // que las líneas → las filas hijas hacen el mismo lookup.
            const accountsDetail = {};
            Object.entries(response.data.accounts_detail || {}).forEach(([key, accounts]) => {
                accountsDetail[key] = accounts.map(account => ({
                    label: account.label,
                    ...buildPeriodDicts(account.values, state.filterPeriod),
                }));
            });

            // Filas calculadas (totales, márgenes, ratios) en el orden de la
            // estructura: cada una depende solo de cuentas y de filas
            // anteriores (un total antes que los márgenes que lo usan).
            const headers = getters.getHeadersTable();

            structure.filter(row => row.kind !== "account").forEach(({ key }) => {
                const config = tableRowHeaders[key];

                headers.forEach(header => {
                    const val = calculateKey(
                        tableRowHeaders,
                        config.keysSum || [],
                        config.keysSubtract || [],
                        config.keyDivide || "",
                        header
                    );

                    if (config.isMoney) {
                        tableRowHeaders[key].itemsDict[header] = val;
                        // YoY sólo se muestra en filas de dinero (las de ratio no
                        // llevan tooltip), así que su prev-year se computa igual
                        // que el valor pero sobre `prevYearDict` de las cuentas
                        // crudas — todas las filas calculadas de dinero (EBITDA,
                        // gastos_totales, no_operacional, utilidad) dependen sólo
                        // de cuentas crudas, ya pobladas en la fase anterior.
                        tableRowHeaders[key].prevYearDict[header] = calculateKey(
                            tableRowHeaders,
                            config.keysSum || [],
                            config.keysSubtract || [],
                            config.keyDivide || "",
                            header,
                            "prevYearDict"
                        );
                        // Mismo cálculo para el peer del período anterior (MoM/QoQ).
                        tableRowHeaders[key].prevPeriodDict[header] = calculateKey(
                            tableRowHeaders,
                            config.keysSum || [],
                            config.keysSubtract || [],
                            config.keyDivide || "",
                            header,
                            "prevPeriodDict"
                        );
                    } else {
                        // Filas de ratio (margen %, gastos/ventas %): porcentaje
                        // ENTERO (regla del sistema, ver CLAUDE.md #9). Guardamos
                        // un number redondeado, no un string `.toFixed` — el dato
                        // crudo debe ser consistente con las filas de dinero.
                        tableRowHeaders[key].itemsDict[header] = Math.round(val * 100);
                    }
                });
            });

            commit("SET_REPORT_DATA", {
                tableRowHeaders,
                netSellsDetail: response.data.ventas_netas_detail,
                maxCostsDetail: response.data.max_costs_detail,
                maxExpensesDetail: response.data.max_expenses_detail,
                accountsDetail,
            });
            commit("TOGGLE_CHILD_ROWS", false)
        } catch (error) {
            consoleHandlerError({ error, prefix: "[resultsReport]", silent: true });
            commit("SET_ERROR", extractErrorMessage(error, "Error al cargar el reporte."));
        } finally {
            commit("SET_LOADING", false);
        }
    }
};

// Proyecta las entries `[{period, total_amount, prev_year_amount,
// prev_period_amount}]` del backend a los tres dicts keyed por cabecera.
//
// La key DEBE salir de `formatPeriodLabel` — la misma fuente que arma las
// cabeceras (`buildHeadersByPeriod`) contra las que la tabla hace el lookup
// `itemsDict[header]`. Formatear a mano dejaba la key en minúscula ("ene
// 2025") mientras la cabecera venía capitalizada ("Ene 2025"), y el lookup
// fallaba → montos en cero. Mismo patrón que costsReport/expensesReport/etc.
//
// Los peers se guardan crudos con `null` preservado: es el peer del año
// anterior (mismo bucket 12m atrás, denominador del YoY del tooltip) y el del
// período anterior (MoM/QoQ, da peer real a la primera columna). `null` = sin
// peer (cuenta sin movimiento entonces); el tooltip lo pinta '—', no '0'.
function buildPeriodDicts(entries, filterPeriod) {
    const itemsDict = {};
    const prevYearDict = {};
    const prevPeriodDict = {};
    (entries || []).forEach(item => {
        const keyPeriod = formatPeriodLabel(item.period, filterPeriod);
        itemsDict[keyPeriod] = Number(item.total_amount);
        prevYearDict[keyPeriod] = item.prev_year_amount == null ? null : Number(item.prev_year_amount);
        prevPeriodDict[keyPeriod] = item.prev_period_amount == null ? null : Number(item.prev_period_amount);
    });
    return { itemsDict, prevYearDict, prevPeriodDict };
}

// `dictField` permite correr la misma fórmula sobre el dict del año anterior
// (`prevYearDict`) para derivar el prev-year de las filas calculadas, sin
// duplicar la lógica de suma/resta/división. Default `itemsDict` = valor actual.
function calculateKey(tableRowHeaders, keysSum, keysSubtract, keyDivide, columnHeader, dictField = "itemsDict") {
    let sumAmount = 0;
    keysSum.forEach(key => {
        sumAmount += Number((tableRowHeaders[key] ?? {})[dictField]?.[columnHeader] ?? 0);
    });

    let subtractAmount = 0;
    keysSubtract.forEach(key => {
        subtractAmount += Number((tableRowHeaders[key] ?? {})[dictField]?.[columnHeader] ?? 0);
    });

    let divideAmount = 1;
    if (keyDivide !== "") {
        divideAmount = Number((tableRowHeaders[keyDivide] ?? {})[dictField]?.[columnHeader] ?? 0);
    }

    if (divideAmount === 0) return 0;
    return (sumAmount - subtractAmount) / (keyDivide !== "" ? divideAmount : 1);
}

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
};
